import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inventoryApi } from '../api/inventoryApi'
import { notify } from '@/shared/utils'
import type { InventoryItem, FoodItem, ClothingItem, CreateFoodItemInput, EditFoodItemInput, CreateClothingProductInput, EditClothingProductInput } from '@/shared/types'

export function useInventory(vendorId: string) {
  const queryClient = useQueryClient()

  const inventoryQuery = useQuery({
    queryKey: ['inventory', vendorId],
    queryFn: () => inventoryApi.fetchInventory(vendorId),
    enabled: !!vendorId,
  })

  const createFoodMutation = useMutation({
    mutationFn: ({ input, imageFile }: { input: CreateFoodItemInput; imageFile: File }) =>
      inventoryApi.createFoodItem(input, imageFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', vendorId] })
      notify.success('Food item created successfully')
    },
    onError: (error: { message?: string }) => {
      notify.error(error?.message || 'Failed to create food item')
    },
  })

  const updateFoodMutation = useMutation({
    mutationFn: ({ input, imageFile }: { input: EditFoodItemInput; imageFile?: File }) =>
      inventoryApi.updateFoodItem(input, imageFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', vendorId] })
      notify.success('Food item updated successfully')
    },
    onError: (error: { message?: string }) => {
      notify.error(error?.message || 'Failed to update food item')
    },
  })

  const toggleAvailabilityMutation = useMutation({
    mutationFn: (item: FoodItem) => inventoryApi.toggleFoodItemAvailability(item, vendorId),
    onSuccess: (_data, item) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', vendorId] })
      notify.success(`Item marked as ${!item.isAvailable ? 'available' : 'unavailable'}`)
    },
    onError: () => {
      notify.error('Failed to update availability')
    },
  })

  const deleteFoodMutation = useMutation({
    mutationFn: (itemId: string) => inventoryApi.deleteFoodItem(vendorId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', vendorId] })
      notify.success('Item deleted successfully')
    },
    onError: (error: { message?: string }) => {
      notify.error(error?.message || 'Failed to delete item')
    },
  })

  const createClothingMutation = useMutation({
    mutationFn: ({ input, imageFiles }: { input: CreateClothingProductInput; imageFiles: File[] }) =>
      inventoryApi.createClothingItem(input, imageFiles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', vendorId] })
      notify.success('Clothing product created successfully')
    },
    onError: (error: { message?: string }) => {
      notify.error(error?.message || 'Failed to create clothing product')
    },
  })

  const updateClothingMutation = useMutation({
    mutationFn: ({ input, imageFiles }: { input: EditClothingProductInput; imageFiles?: File[] }) =>
      inventoryApi.updateClothingItem(input, imageFiles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', vendorId] })
      notify.success('Clothing product updated successfully')
    },
    onError: (error: { message?: string }) => {
      notify.error(error?.message || 'Failed to update clothing product')
    },
  })

  const deleteClothingMutation = useMutation({
    mutationFn: (itemId: string) => inventoryApi.deleteClothingItem(vendorId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', vendorId] })
      notify.success('Item deleted successfully')
    },
    onError: (error: { message?: string }) => {
      notify.error(error?.message || 'Failed to delete item')
    },
  })

  const handleAddEdit = async (
    item: InventoryItem,
    vendorType: 'FOOD' | 'CLOTHING',
    editItem: InventoryItem | null,
    imageFile?: File,
    imageFiles?: File[]
  ) => {
    const isEdit = !!editItem

    if (vendorType === 'FOOD') {
      const foodItem = item as FoodItem
      if (isEdit) {
        await updateFoodMutation.mutateAsync({
          input: {
            vendorId,
            itemId: foodItem.id,
            name: foodItem.name,
            description: foodItem.description || '',
            price: foodItem.price,
            isAvailable: foodItem.isAvailable,
            categories: foodItem.categories?.map((c) => c.id) || [],
          },
          imageFile,
        })
      } else {
        await createFoodMutation.mutateAsync({
          input: {
            vendorId,
            name: foodItem.name,
            description: foodItem.description || '',
            price: foodItem.price,
            isAvailable: foodItem.isAvailable,
            imageUrl: '',
            categories: foodItem.categories?.map((c) => c.id) || [],
          },
          imageFile: imageFile!,
        })
      }
    } else {
      const clothingItem = item as ClothingItem
      
      // Build variant data with proper image index mapping
      // imageFiles contains: [primaryImg1, additionalImg1a, additionalImg1b, primaryImg2, additionalImg2a, ...]
      // We need to track which index each image is at
      let currentFileIndex = 0
      const variantData = clothingItem.variants.map((v) => {
        const variantImageUrl = typeof v.imageUrl === 'string' ? v.imageUrl : ''
        // A blob: URL means this variant has a newly uploaded primary image
        const isNewPrimaryImage = variantImageUrl.startsWith('blob:')
        
        // Track primary image index
        const primaryImageIndex = isNewPrimaryImage ? currentFileIndex : undefined
        if (isNewPrimaryImage) {
          currentFileIndex++
        }
        
        // Track additional image indices
        const additionalImageIndices: number[] = []
        const existingAdditionalUrls: string[] = []
        
        if (v.imageUrls && v.imageUrls.length > 0) {
          for (const imgUrl of v.imageUrls) {
            if (imgUrl.startsWith('blob:')) {
              // This is a new additional image
              additionalImageIndices.push(currentFileIndex)
              currentFileIndex++
            } else {
              // This is an existing additional image to keep
              existingAdditionalUrls.push(imgUrl)
            }
          }
        }
        
        return {
          size: v.size?.trim() || '',
          color: v.color?.trim() || '',
          price: typeof v.price === 'number' ? v.price : Number(v.price) || 0,
          stockQty: typeof v.stockQty === 'number' ? v.stockQty : Number(v.stockQty) || 0,
          imageIndex: primaryImageIndex,
          existingImageUrl: !isNewPrimaryImage && variantImageUrl ? variantImageUrl : undefined,
          additionalImageIndices: additionalImageIndices.length > 0 ? additionalImageIndices : undefined,
          existingAdditionalUrls: existingAdditionalUrls.length > 0 ? existingAdditionalUrls : undefined,
        }
      })

      if (isEdit) {
        await updateClothingMutation.mutateAsync({
          input: {
            vendorId,
            productId: clothingItem.id,
            name: clothingItem.name?.trim() || '',
            description: clothingItem.description?.trim() || '',
            brand: clothingItem.brand?.trim() || '',
            variant: variantData,
            categories: clothingItem.categories?.map((c) => c.id) || [],
          },
          imageFiles,
        })
      } else {
        await createClothingMutation.mutateAsync({
          input: {
            vendorId,
            name: clothingItem.name?.trim() || '',
            description: clothingItem.description?.trim() || '',
            brand: clothingItem.brand?.trim() || '',
            variant: variantData,
            categories: clothingItem.categories?.map((c) => c.id) || [],
          },
          imageFiles: imageFiles || [],
        })
      }
    }
  }

  return {
    inventory: inventoryQuery.data || [],
    isLoading: inventoryQuery.isLoading,
    refetch: inventoryQuery.refetch,
    handleAddEdit,
    toggleAvailability: toggleAvailabilityMutation.mutate,
    deleteFood: deleteFoodMutation.mutate,
    deleteClothing: deleteClothingMutation.mutate,
    isProcessing:
      createFoodMutation.isPending ||
      updateFoodMutation.isPending ||
      toggleAvailabilityMutation.isPending ||
      deleteFoodMutation.isPending ||
      createClothingMutation.isPending ||
      updateClothingMutation.isPending ||
      deleteClothingMutation.isPending,
  }
}
